import { normalizeSortKey } from '@/lib/shareState';
import type { CatalogFilters, PriceOverride, SortDirection, SortKey } from '@/types/data';

const STORAGE_VERSION = 2;
const OVERRIDES_KEY = `boozeratio.priceOverrides.v${STORAGE_VERSION}`;
const PREFS_KEY = `boozeratio.prefs.v${STORAGE_VERSION}`;
const BASELINE_KEY = `boozeratio.baseline.v${STORAGE_VERSION}`;

export interface UserPrefs {
  sortKey: SortKey;
  sortDirection: SortDirection;
  filters: Partial<CatalogFilters>;
  volumeUnit: 'ml' | 'fl_oz';
  baselineScope: 'subtype' | 'category' | 'all';
}

const defaultPrefs: UserPrefs = {
  sortKey: 'alcoholPer100Calories',
  sortDirection: 'desc',
  filters: { category: 'all' },
  volumeUnit: 'fl_oz',
  baselineScope: 'category',
};

function canUseStorage(): boolean {
  try {
    const k = '__pm_test__';
    localStorage.setItem(k, '1');
    localStorage.removeItem(k);
    return true;
  } catch {
    return false;
  }
}

function readJson<T>(key: string): T | null {
  if (!canUseStorage()) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown): boolean {
  if (!canUseStorage()) return false;
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

export function loadPriceOverrides(): Record<string, PriceOverride> {
  return readJson<Record<string, PriceOverride>>(OVERRIDES_KEY) ?? {};
}

export function savePriceOverride(override: PriceOverride): boolean {
  const all = loadPriceOverrides();
  all[override.productId] = { ...override, version: STORAGE_VERSION };
  return writeJson(OVERRIDES_KEY, all);
}

export function resetPriceOverride(productId: string): boolean {
  const all = loadPriceOverrides();
  delete all[productId];
  return writeJson(OVERRIDES_KEY, all);
}

export function resetAllPriceOverrides(): boolean {
  return writeJson(OVERRIDES_KEY, {});
}

export function exportPriceOverridesJson(): string {
  return JSON.stringify(
    {
      version: STORAGE_VERSION,
      exportedAt: new Date().toISOString(),
      overrides: loadPriceOverrides(),
    },
    null,
    2,
  );
}

export function importPriceOverridesJson(raw: string): {
  ok: boolean;
  count: number;
  error?: string;
} {
  try {
    const parsed = JSON.parse(raw) as {
      version?: number;
      overrides?: Record<string, PriceOverride>;
    };
    if (!parsed.overrides || typeof parsed.overrides !== 'object') {
      return { ok: false, count: 0, error: 'Missing overrides object' };
    }
    const cleaned: Record<string, PriceOverride> = {};
    for (const [id, o] of Object.entries(parsed.overrides)) {
      if (
        o &&
        typeof o.packagePriceUsd === 'number' &&
        o.packagePriceUsd > 0 &&
        Number.isInteger(o.containerCount) &&
        o.containerCount > 0 &&
        typeof o.containerMl === 'number' &&
        o.containerMl > 0
      ) {
        cleaned[id] = {
          productId: o.productId || id,
          packagePriceUsd: o.packagePriceUsd,
          containerCount: o.containerCount,
          containerMl: o.containerMl,
          includeTaxDeposit: Boolean(o.includeTaxDeposit),
          updatedAt: o.updatedAt || new Date().toISOString(),
          version: STORAGE_VERSION,
        };
      }
    }
    writeJson(OVERRIDES_KEY, cleaned);
    return { ok: true, count: Object.keys(cleaned).length };
  } catch {
    return { ok: false, count: 0, error: 'Corrupt or invalid JSON' };
  }
}

export function loadPrefs(): UserPrefs {
  const stored = readJson<Partial<UserPrefs>>(PREFS_KEY);
  if (!stored) return { ...defaultPrefs, filters: { ...defaultPrefs.filters } };
  const sortKey =
    normalizeSortKey(stored.sortKey) ?? defaultPrefs.sortKey;
  return {
    ...defaultPrefs,
    ...stored,
    sortKey,
    filters: { ...defaultPrefs.filters, ...stored.filters },
  };
}

export function savePrefs(prefs: UserPrefs): boolean {
  return writeJson(PREFS_KEY, prefs);
}

export function loadBaselineId(): string | null {
  return readJson<string>(BASELINE_KEY);
}

export function saveBaselineId(productId: string | null): boolean {
  if (productId == null) {
    if (!canUseStorage()) return false;
    try {
      localStorage.removeItem(BASELINE_KEY);
      return true;
    } catch {
      return false;
    }
  }
  return writeJson(BASELINE_KEY, productId);
}

export { canUseStorage, defaultPrefs };
